import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import bePutSendSms from 'actions/salon/bePutSendSms.action';
import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import bePutClientSmsText from 'actions/sms/bePutClientSmsText.action';
import bePutSalonFlagSettings from 'actions/sms/bePutSalonFlagSettings.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import Space from 'components/generic/Space.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import IN from 'constants/in';
import SMS_TYPE from 'constants/salon/smsType.const';
import S from 'constants/string.const';
import useAfn from 'hooks/useAfn.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import {
  selectCurrentSalonBilling,
  selectCurrentSalonConfirmationSms,
  selectCurrentSalonDigitalCardAddOn,
  selectCurrentSalonId,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectBillingInvoices, selectBillingPlan } from 'selectors/settings.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import { hasOverdueInvoices } from 'utils/salons/hasOverdueInvoices.util';

import { GetCustomerInvoices } from 'settings/billing/actions';
import { OverdueInvoiceModal } from 'settings/billing/components/OverdueInvoiceModal.component';

import { DacModalContent } from './DacModalContent.component';

import CN from './SmsPreferencesPopup.module.scss';

const L = logger('SmsPreferencesPopup');

const LINK = 'https://help.getslick.com/en/articles/9036772-digital-appointment-cards';

const SmsPreferencesPopup = ({ salonId, hasBilling, bePutSendSms, isDac, defaultSmsType, bePutSalonFlagSettings, isReceptionist }) => {
  const { val: isModalBillingOpen, yay: showModalBilling, nay: hideModalBilling } = useTip(false);
  const { val: isModalOpen, yay: showModal, nay: hideModal } = useTip(false);
  const { val: isSuccessModalOpen, yay: showSuccessModal, nay: hideSuccessModal } = useTip(false);
  const { val: isOverdueModalOpen, yay: showOverdueModal, nay: hideOverdueModal } = useTip(false);
  const { inv: isAddonDisabled, set: setConfirmation } = useTip(isDac);

  const { val: selectYes, tip: toggleSelectYes } = useTip(true);
  const { val: selectNo, tip: toggleSelectNo } = useTip(false);
  const [smsType, setSmsType] = useState(defaultSmsType);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const invoices = useSelector(selectBillingInvoices);

  const [sendSmsConfirmationResult, handleSendConfirmation] = useAfn(async () => {
    if (hasOverdueInvoices(invoices)) {
      hideModal();
      showOverdueModal();
      return;
    }

    const digitalCardResult = await bePutSendSms({
      salonId,
      digital_card_add_on: isAddonDisabled,
    });
    if (!digitalCardResult.is.ok) {
      L.warn('handleSendConfirmation()', 'digitalCardResult:', digitalCardResult);
      return;
    }

    setConfirmation(digitalCardResult.json.digital_card_add_on);

    const sendConfirm = SMS_TYPE.onRequested;

    const sendResult = await bePutSalonFlagSettings({ data: { send_confirmation_sms: sendConfirm } });
    if (!sendResult.is.ok) {
      L.warn('handleSendConfirmation()', 'sendResult:', sendResult);
      return;
    }
    setSmsType(sendResult.json.send_confirmation_sms);
    hideModal();
    showSuccessModal();
    L.unignore('handleSendConfirmation()', 'fetchSalon:', fetchSalon(salonId));
  }, [bePutSendSms, salonId, isAddonDisabled, setConfirmation, bePutSalonFlagSettings, hideModal, showSuccessModal, invoices, showOverdueModal]);

  const [handleRejectConfirmationResult, handleRejectConfirmation] = useAfn(async () => {
    const sendConfirm = SMS_TYPE.offRequested;
    const sendResult = await bePutSalonFlagSettings({ data: { send_confirmation_sms: sendConfirm } });
    if (!sendResult.is.ok) {
      L.warn('handleRejectConfirmation()', 'sendResult:', sendResult);
      return;
    }
    setSmsType(sendResult.json.send_confirmation_sms);
    hideModal();
    L.unignore('handleRejectConfirmation()', 'fetchSalon:', fetchSalon(salonId));
  }, [bePutSalonFlagSettings, salonId, hideModal]);

  const handleSelection = useCallback(() => {
    toggleSelectYes();
    toggleSelectNo();
  }, [toggleSelectYes, toggleSelectNo]);

  const invoicesLoaded = invoices && Array.isArray(invoices.results);

  useEffect(() => {
    if (!salonId) return;
    dispatch(GetCustomerInvoices(salonId, {}));
  }, [dispatch, salonId]);

  return (
    <div data-bem="SmsPreferencesPopup" className={CN.component}>
      {smsType === SMS_TYPE.offUnspecified && hasBilling && (
        <div className={CN.smsBanner}>
          <div className={CN.messageContainer}>
            <div className={CN.message}>
              {S.rocket} <Space> {t('scheduler.smsPreferencesPopup.newFutureBanner.description')}</Space>
            </div>
            <Button variant="emphasis" width="small" onClick={!isReceptionist ? showModal : alwaysNoop} disabled={!invoicesLoaded}>
              <div className={CN.buttonContent}>
                {!invoicesLoaded && <InlineSpinner variant="small" />}
                <div className={CN.buttonFont}>{t('scheduler.smsPreferencesPopup.newFutureBanner.learnMoreButton')}</div>
              </div>
            </Button>
            <TooltipForReceptionist where={IN.smsBanner} />
          </div>
          <div className={CN.closeBannerButton}>
            <Button variant="empty" onClick={!isReceptionist ? showModal : alwaysNoop}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </div>
      )}

      {fromAnyToBool(isModalOpen) && (
        <SalonModal tag="SmsPreferencesPopup__modal" isOpen={isModalOpen} withFooter variant="large" mode="dark" banner="backgroundImage">
          <SalonModal.Header>
            <div className={CN.modalHeader}>
              <div className={CN.title}>{t('scheduler.smsPreferencesPopup.clientPaperCardModal.title')}</div>
              <div className={CN.subTitle}>{t('scheduler.smsPreferencesPopup.clientPaperCardModal.subtitle')}</div>
            </div>
            <div className={CN.closeModalButton}>
              <Button variant="empty" onClick={hasBilling ? handleRejectConfirmation : hideModal}>
                <Icon variant="close" />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalBody}>
              {(sendSmsConfirmationResult.isLoading || handleRejectConfirmationResult.isLoading) && <OverlaySpinner />}
              <DacModalContent
                selectNo={selectNo}
                selectYes={selectYes}
                onEnableDac={hasBilling ? handleSendConfirmation : showModalBilling}
                onRejectDac={handleRejectConfirmation}
                handleSelection={handleSelection}
              />
            </div>
          </SalonModal.Body>
          <SalonModal.Footer>
            <div className={CN.modalFooter}>
              <div className={CN.notes}>
                <ExternalLink link={LINK} variant="inline">
                  <span className={CN.linkClass}>
                    {S.space}
                    {t('scheduler.smsPreferencesPopup.clientPaperCardModal.clickHere')}
                  </span>
                </ExternalLink>
                {t('scheduler.smsPreferencesPopup.clientPaperCardModal.note')}
              </div>
            </div>
          </SalonModal.Footer>
        </SalonModal>
      )}

      {fromAnyToBool(isModalBillingOpen) && (
        <SalonModal tag="SmsPreferencesPopup__modal--billing" isOpen variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={hideModalBilling}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <IconStatus variant="alert">
                <Icon variant="alert" />
              </IconStatus>
            </div>
            <div className={CN.modalContent}>
              <div className={CN.title}>{t('scheduler.smsPreferencesPopup.cardDetailsRequiredModal.title')}</div>
              <span className={CN.modalText}>{t('scheduler.smsPreferencesPopup.cardDetailsRequiredModal.description')}</span>
            </div>
          </SalonModal.Body>
        </SalonModal>
      )}

      {fromAnyToBool(isSuccessModalOpen) && (
        <SalonModal tag="SmsPreferencesPopup__modal--success" isOpen variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={hideSuccessModal}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <div className={CN.modalImage}>
              <IconStatus variant="success">
                <Icon variant="confirm" width="lg" />
              </IconStatus>
            </div>
            <div className={CN.modalContent}>
              <div className={CN.title}>{t('scheduler.smsPreferencesPopup.digitalAppointmentCardModal.title')}</div>
              <span className={CN.modalText}>{t('scheduler.smsPreferencesPopup.digitalAppointmentCardModal.description')}</span>
            </div>
          </SalonModal.Body>
        </SalonModal>
      )}

      <OverdueInvoiceModal tag="SmsPreferencesPopup__modal--overdue-invoice" isOpen={isOverdueModalOpen} onClose={hideOverdueModal} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  billingPlan: selectBillingPlan(state),
  hasBilling: selectCurrentSalonBilling(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
  isDac: selectCurrentSalonDigitalCardAddOn(state),
  defaultSmsType: selectCurrentSalonConfirmationSms(state),
  isReceptionist: isEffectiveReceptionist(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      bePutSalonFlagSettings,
      beGetClientSmsText,
      bePutClientSmsText,
      fetchSalon,
      bePutSendSms,
    },
    dispatch,
  );

const SmsPreferencesPopupHoc = connect(mapStateToProps, mapDispatchToProps)(SmsPreferencesPopup);

export default SmsPreferencesPopupHoc;
